import { signOut as firebaseSignOut, onAuthStateChanged, onIdTokenChanged, signInWithCustomToken } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { auth, existingAuth, functions } from '../services/firebase';
import { ensureUserRegistered } from '../services/registration';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isExchangingTokenRef = useRef(false);

  const exchangeToken = useCallback(async (existingUser) => {
    if (auth.currentUser?.uid === existingUser.uid) return;
    if (isExchangingTokenRef.current) return;

    isExchangingTokenRef.current = true;
    try {
      const idToken = await existingUser.getIdToken();
      const exchangeTokenFn = httpsCallable(functions, 'exchangeToken');
      const result = await exchangeTokenFn({ idToken });
      await signInWithCustomToken(auth, result.data.customToken);
      await ensureUserRegistered(existingUser.uid);
    } catch (error) {
      console.error('Token exchange failed:', error);
    } finally {
      isExchangingTokenRef.current = false;
    }
  }, []);

  // 既存プロジェクトの認証状態を監視し、トークン交換を実行
  useEffect(() => {
    const unsubscribe = onIdTokenChanged(existingAuth, async (existingUser) => {
      if (existingUser) {
        await exchangeToken(existingUser);
      }
    });
    return () => unsubscribe();
  }, [exchangeToken]);

  // 新プロジェクトの認証状態を監視（メインの状態）
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (newUser) => {
      setUser(newUser);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  // 初期ロード時に既存プロジェクトのユーザーがいればトークン交換
  useEffect(() => {
    const checkExistingAuth = async () => {
      const existingUser = existingAuth.currentUser;
      if (existingUser && !auth.currentUser) {
        await exchangeToken(existingUser);
      }
    };
    checkExistingAuth();
  }, [exchangeToken]);

  const signInWithGoogleCode = async (code) => {
    try {
      const signInWithGoogleFn = httpsCallable(functions, 'signInWithGoogle');
      const redirectUri = window.location.origin + window.location.pathname;
      const result = await signInWithGoogleFn({ code, redirectUri });

      await signInWithCustomToken(auth, result.data.mainCustomToken);
      const existingUserCredential = await signInWithCustomToken(existingAuth, result.data.existingCustomToken);
      await ensureUserRegistered(existingUserCredential.user.uid);
    } catch (error) {
      await Promise.all([firebaseSignOut(auth).catch(() => {}), firebaseSignOut(existingAuth).catch(() => {})]);
      console.error('Google sign-in failed:', error);
      throw error;
    }
  };

  const signInWithAppleCode = async (tempCode, clientId) => {
    try {
      const signInWithAppleFn = httpsCallable(functions, 'signInWithApple');
      const result = await signInWithAppleFn({ tempCode, clientId });

      await signInWithCustomToken(auth, result.data.mainCustomToken);
      const existingUserCredential = await signInWithCustomToken(existingAuth, result.data.existingCustomToken);
      await ensureUserRegistered(existingUserCredential.user.uid);
    } catch (error) {
      await Promise.all([firebaseSignOut(auth).catch(() => {}), firebaseSignOut(existingAuth).catch(() => {})]);
      console.error('Apple sign-in failed:', error);
      throw error;
    }
  };

  const signOut = async () => {
    await Promise.all([firebaseSignOut(existingAuth), firebaseSignOut(auth)]);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogleCode,
        signInWithAppleCode,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext };
